import type { CSSProperties } from 'react';

type CssVars = CSSProperties & Record<`--${string}`, string | number>;

export type PortalThemeTemplate =
  | 'haulier'
  | 'transport'
  | 'logistics'
  | 'warehouse'
  | 'total_logistics'
  | 'freight_forwarder';

export interface PortalThemeOption {
  value: PortalThemeTemplate;
  label: string;
  description: string;
  sector: string;
  preview: string;
}

export interface PortalThemeDefinition extends PortalThemeOption {
  cssVars: CssVars;
  shellBackground: CSSProperties;
  textureOverlay: CSSProperties;
  heroOverlay: CSSProperties;
  loginShell: CSSProperties;
  panelSurface: CSSProperties;
  sidebarSurface: CSSProperties;
  footerSurface: CSSProperties;
  badgeSurface: CSSProperties;
  chipSurface: CSSProperties;
}

export type ThemePaletteId = 'ocean' | 'signal_blue' | 'teal' | 'emerald' | 'amber' | 'coral' | 'rose' | 'slate';

export interface StorefrontVisualThemeInput {
  palette?: string | null;
  tone?: string | null;
  visualPattern?: string | null;
}

interface RuntimePalette {
  id: ThemePaletteId;
  surface: string;
  surfaceSoft: string;
  surfaceStrong: string;
  motifMain: string;
  motifSoft: string;
  motifMuted: string;
  text: string;
  brandVars: Pick<CssVars,
    '--color-brand-50' | '--color-brand-100' | '--color-brand-200' | '--color-brand-300' | '--color-brand-400' |
    '--color-brand-500' | '--color-brand-600' | '--color-brand-700' | '--color-brand-800' | '--color-brand-900'
  >;
}

const RUNTIME_PALETTES: Record<ThemePaletteId, RuntimePalette> = {
  ocean: {
    id: 'ocean',
    surface: '#eff4ff',
    surfaceSoft: '#dfe8ff',
    surfaceStrong: '#0b1838',
    motifMain: '#1f398d',
    motifSoft: '#29b8da',
    motifMuted: '#bfd2ff',
    text: '#0b1838',
    brandVars: {
      '--color-brand-50': '#eff4ff',
      '--color-brand-100': '#dfe8ff',
      '--color-brand-200': '#bfd2ff',
      '--color-brand-300': '#90b0ff',
      '--color-brand-400': '#6687f2',
      '--color-brand-500': '#4360d2',
      '--color-brand-600': '#2b4ab3',
      '--color-brand-700': '#1f398d',
      '--color-brand-800': '#15295f',
      '--color-brand-900': '#0b1838',
    },
  },
  signal_blue: {
    id: 'signal_blue',
    surface: '#eef7ff',
    surfaceSoft: '#d8ecff',
    surfaceStrong: '#101d35',
    motifMain: '#165084',
    motifSoft: '#28c1ee',
    motifMuted: '#afd8ff',
    text: '#101d35',
    brandVars: {
      '--color-brand-50': '#eef7ff',
      '--color-brand-100': '#d8ecff',
      '--color-brand-200': '#afd8ff',
      '--color-brand-300': '#7fc0fb',
      '--color-brand-400': '#4ba3ec',
      '--color-brand-500': '#277ec6',
      '--color-brand-600': '#1b65aa',
      '--color-brand-700': '#165084',
      '--color-brand-800': '#14385b',
      '--color-brand-900': '#101d35',
    },
  },
  teal: {
    id: 'teal',
    surface: '#effdf9',
    surfaceSoft: '#d5f8ee',
    surfaceStrong: '#0e2924',
    motifMain: '#146452',
    motifSoft: '#3fc0d4',
    motifMuted: '#a9efdb',
    text: '#0e2924',
    brandVars: {
      '--color-brand-50': '#effdf9',
      '--color-brand-100': '#d5f8ee',
      '--color-brand-200': '#a9efdb',
      '--color-brand-300': '#72dfc0',
      '--color-brand-400': '#36c59f',
      '--color-brand-500': '#1b9f7d',
      '--color-brand-600': '#157f64',
      '--color-brand-700': '#146452',
      '--color-brand-800': '#14483d',
      '--color-brand-900': '#0e2924',
    },
  },
  emerald: {
    id: 'emerald',
    surface: '#ecfdf5',
    surfaceSoft: '#dcfce7',
    surfaceStrong: '#064e3b',
    motifMain: '#047857',
    motifSoft: '#22c55e',
    motifMuted: '#bbf7d0',
    text: '#064e3b',
    brandVars: {
      '--color-brand-50': '#ecfdf5',
      '--color-brand-100': '#dcfce7',
      '--color-brand-200': '#bbf7d0',
      '--color-brand-300': '#86efac',
      '--color-brand-400': '#4ade80',
      '--color-brand-500': '#22c55e',
      '--color-brand-600': '#16a34a',
      '--color-brand-700': '#047857',
      '--color-brand-800': '#065f46',
      '--color-brand-900': '#064e3b',
    },
  },
  amber: {
    id: 'amber',
    surface: '#fffbeb',
    surfaceSoft: '#fef3c7',
    surfaceStrong: '#451a03',
    motifMain: '#92400e',
    motifSoft: '#f59e0b',
    motifMuted: '#fde68a',
    text: '#451a03',
    brandVars: {
      '--color-brand-50': '#fffbeb',
      '--color-brand-100': '#fef3c7',
      '--color-brand-200': '#fde68a',
      '--color-brand-300': '#fcd34d',
      '--color-brand-400': '#fbbf24',
      '--color-brand-500': '#f59e0b',
      '--color-brand-600': '#d97706',
      '--color-brand-700': '#92400e',
      '--color-brand-800': '#78350f',
      '--color-brand-900': '#451a03',
    },
  },
  coral: {
    id: 'coral',
    surface: '#fff7ed',
    surfaceSoft: '#ffedd5',
    surfaceStrong: '#7c2d12',
    motifMain: '#c2410c',
    motifSoft: '#fb923c',
    motifMuted: '#fed7aa',
    text: '#7c2d12',
    brandVars: {
      '--color-brand-50': '#fff7ed',
      '--color-brand-100': '#ffedd5',
      '--color-brand-200': '#fed7aa',
      '--color-brand-300': '#fdba74',
      '--color-brand-400': '#fb923c',
      '--color-brand-500': '#f97316',
      '--color-brand-600': '#ea580c',
      '--color-brand-700': '#c2410c',
      '--color-brand-800': '#9a3412',
      '--color-brand-900': '#7c2d12',
    },
  },
  rose: {
    id: 'rose',
    surface: '#fff1f2',
    surfaceSoft: '#ffe4e6',
    surfaceStrong: '#881337',
    motifMain: '#be123c',
    motifSoft: '#fb7185',
    motifMuted: '#fecdd3',
    text: '#881337',
    brandVars: {
      '--color-brand-50': '#fff1f2',
      '--color-brand-100': '#ffe4e6',
      '--color-brand-200': '#fecdd3',
      '--color-brand-300': '#fda4af',
      '--color-brand-400': '#fb7185',
      '--color-brand-500': '#f43f5e',
      '--color-brand-600': '#e11d48',
      '--color-brand-700': '#be123c',
      '--color-brand-800': '#9f1239',
      '--color-brand-900': '#881337',
    },
  },
  slate: {
    id: 'slate',
    surface: '#f8fafc',
    surfaceSoft: '#e2e8f0',
    surfaceStrong: '#0f172a',
    motifMain: '#334155',
    motifSoft: '#64748b',
    motifMuted: '#cbd5e1',
    text: '#0f172a',
    brandVars: {
      '--color-brand-50': '#f8fafc',
      '--color-brand-100': '#f1f5f9',
      '--color-brand-200': '#e2e8f0',
      '--color-brand-300': '#cbd5e1',
      '--color-brand-400': '#94a3b8',
      '--color-brand-500': '#64748b',
      '--color-brand-600': '#475569',
      '--color-brand-700': '#334155',
      '--color-brand-800': '#1e293b',
      '--color-brand-900': '#0f172a',
    },
  },
};

function resolveRuntimePalette(value?: string | null): RuntimePalette {
  return RUNTIME_PALETTES[(value ?? 'ocean') as ThemePaletteId] ?? RUNTIME_PALETTES.ocean;
}

function alpha(hex: string, opacity: number): string {
  const raw = hex.replace('#', '');
  const full = raw.length === 3 ? raw.split('').map((char) => `${char}${char}`).join('') : raw;
  const int = Number.parseInt(full, 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

const THEMES: Record<PortalThemeTemplate, PortalThemeDefinition> = {
  haulier: {
    value: 'haulier',
    label: 'Haulier',
    description: 'Coal, amber and lane stripes for container haulage and depot moves.',
    sector: 'Haulier',
    preview: 'from-stone-900 via-amber-700 to-orange-500',
    cssVars: {
      '--color-brand-50': '#f9f6ef',
      '--color-brand-100': '#f2e8d4',
      '--color-brand-200': '#e4cc9e',
      '--color-brand-300': '#d5ab67',
      '--color-brand-400': '#bf863f',
      '--color-brand-500': '#9f6526',
      '--color-brand-600': '#80501f',
      '--color-brand-700': '#603d1a',
      '--color-brand-800': '#392714',
      '--color-brand-900': '#1a1817',
      '--color-ocean-50': '#f4f7f7',
      '--color-ocean-100': '#dde7e7',
      '--color-ocean-200': '#b7caca',
      '--color-ocean-300': '#8ea9a8',
      '--color-ocean-400': '#648685',
      '--color-ocean-500': '#456c6b',
      '--color-ocean-600': '#355655',
      '--color-ocean-700': '#294342',
    },
    shellBackground: {
      background: 'linear-gradient(180deg, rgba(251,247,240,0.94), rgba(245,241,235,0.98))',
    },
    textureOverlay: {
      backgroundImage: 'repeating-linear-gradient(135deg, rgba(191,134,63,0.08) 0 16px, transparent 16px 38px)',
      opacity: 0.8,
    },
    heroOverlay: {
      background: 'linear-gradient(90deg, rgba(26,24,23,0.9), rgba(96,61,26,0.72), rgba(191,134,63,0.28))',
    },
    loginShell: {
      background: 'linear-gradient(135deg, #1a1817 0%, #603d1a 52%, #bf863f 100%)',
    },
    panelSurface: {
      background: 'linear-gradient(180deg, rgba(255,252,247,0.96), rgba(248,243,236,0.98))',
      border: '1px solid rgba(191,134,63,0.18)',
    },
    sidebarSurface: {
      background: 'linear-gradient(180deg, rgba(255,252,247,0.96), rgba(247,240,230,1))',
    },
    footerSurface: {
      background: 'linear-gradient(135deg, #1a1817, #392714 58%, #603d1a 100%)',
    },
    badgeSurface: {
      background: 'rgba(255,247,237,0.18)',
      border: '1px solid rgba(255,255,255,0.18)',
    },
    chipSurface: {
      background: 'rgba(255,248,235,0.9)',
      border: '1px solid rgba(191,134,63,0.22)',
    },
  },
  transport: {
    value: 'transport',
    label: 'Transport',
    description: 'Blue motion lines for trucking, fleet and route operations.',
    sector: 'Transport',
    preview: 'from-sky-950 via-blue-700 to-cyan-500',
    cssVars: {
      '--color-brand-50': '#eef7ff',
      '--color-brand-100': '#d8ecff',
      '--color-brand-200': '#afd8ff',
      '--color-brand-300': '#7fc0fb',
      '--color-brand-400': '#4ba3ec',
      '--color-brand-500': '#277ec6',
      '--color-brand-600': '#1b65aa',
      '--color-brand-700': '#165084',
      '--color-brand-800': '#14385b',
      '--color-brand-900': '#101d35',
      '--color-ocean-50': '#eefdfd',
      '--color-ocean-100': '#cff8fb',
      '--color-ocean-200': '#9ceff7',
      '--color-ocean-300': '#5eddff',
      '--color-ocean-400': '#28c1ee',
      '--color-ocean-500': '#0f98c6',
      '--color-ocean-600': '#0d769f',
      '--color-ocean-700': '#0d5f7e',
    },
    shellBackground: {
      background: 'linear-gradient(180deg, rgba(239,248,255,0.94), rgba(242,248,252,0.98))',
    },
    textureOverlay: {
      backgroundImage: 'linear-gradient(120deg, rgba(79,163,236,0.08) 0%, transparent 38%), repeating-linear-gradient(0deg, rgba(15,152,198,0.08) 0 3px, transparent 3px 22px)',
      opacity: 0.9,
    },
    heroOverlay: {
      background: 'linear-gradient(100deg, rgba(16,29,53,0.92), rgba(27,101,170,0.74), rgba(40,193,238,0.30))',
    },
    loginShell: {
      background: 'linear-gradient(135deg, #101d35 0%, #165084 45%, #28c1ee 100%)',
    },
    panelSurface: {
      background: 'linear-gradient(180deg, rgba(251,254,255,0.96), rgba(240,248,255,0.98))',
      border: '1px solid rgba(39,126,198,0.16)',
    },
    sidebarSurface: {
      background: 'linear-gradient(180deg, rgba(251,254,255,0.98), rgba(235,246,255,1))',
    },
    footerSurface: {
      background: 'linear-gradient(135deg, #101d35, #14385b 54%, #165084 100%)',
    },
    badgeSurface: {
      background: 'rgba(255,255,255,0.12)',
      border: '1px solid rgba(255,255,255,0.16)',
    },
    chipSurface: {
      background: 'rgba(239,248,255,0.92)',
      border: '1px solid rgba(39,126,198,0.18)',
    },
  },
  logistics: {
    value: 'logistics',
    label: 'Logistics',
    description: 'Teal routing grid for modern 3PL and coordination portals.',
    sector: 'Logistics',
    preview: 'from-teal-950 via-teal-700 to-emerald-400',
    cssVars: {
      '--color-brand-50': '#effdf9',
      '--color-brand-100': '#d5f8ee',
      '--color-brand-200': '#a9efdb',
      '--color-brand-300': '#72dfc0',
      '--color-brand-400': '#36c59f',
      '--color-brand-500': '#1b9f7d',
      '--color-brand-600': '#157f64',
      '--color-brand-700': '#146452',
      '--color-brand-800': '#14483d',
      '--color-brand-900': '#0e2924',
      '--color-ocean-50': '#eefbfd',
      '--color-ocean-100': '#d3f4fa',
      '--color-ocean-200': '#aaebf5',
      '--color-ocean-300': '#70d9ea',
      '--color-ocean-400': '#3fc0d4',
      '--color-ocean-500': '#239aac',
      '--color-ocean-600': '#1f7686',
      '--color-ocean-700': '#1f5c68',
    },
    shellBackground: {
      background: 'linear-gradient(180deg, rgba(241,252,248,0.95), rgba(237,247,245,0.98))',
    },
    textureOverlay: {
      backgroundImage: 'linear-gradient(rgba(27,159,125,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(35,154,172,0.06) 1px, transparent 1px)',
      backgroundSize: '32px 32px',
      opacity: 0.85,
    },
    heroOverlay: {
      background: 'linear-gradient(100deg, rgba(14,41,36,0.92), rgba(20,100,82,0.78), rgba(63,192,212,0.26))',
    },
    loginShell: {
      background: 'linear-gradient(135deg, #0e2924 0%, #146452 46%, #3fc0d4 100%)',
    },
    panelSurface: {
      background: 'linear-gradient(180deg, rgba(250,255,253,0.96), rgba(239,252,248,0.98))',
      border: '1px solid rgba(27,159,125,0.16)',
    },
    sidebarSurface: {
      background: 'linear-gradient(180deg, rgba(250,255,253,0.98), rgba(234,249,244,1))',
    },
    footerSurface: {
      background: 'linear-gradient(135deg, #0e2924, #14483d 48%, #146452 100%)',
    },
    badgeSurface: {
      background: 'rgba(255,255,255,0.12)',
      border: '1px solid rgba(255,255,255,0.14)',
    },
    chipSurface: {
      background: 'rgba(239,253,249,0.92)',
      border: '1px solid rgba(27,159,125,0.18)',
    },
  },
  warehouse: {
    value: 'warehouse',
    label: 'Warehouse',
    description: 'Copper, sand and block grids for storage and inventory portals.',
    sector: 'Warehouse',
    preview: 'from-neutral-950 via-orange-700 to-amber-400',
    cssVars: {
      '--color-brand-50': '#fff8ef',
      '--color-brand-100': '#fcebd2',
      '--color-brand-200': '#f3d29f',
      '--color-brand-300': '#e8b36b',
      '--color-brand-400': '#d58a3b',
      '--color-brand-500': '#b86924',
      '--color-brand-600': '#8f521f',
      '--color-brand-700': '#6b3f1c',
      '--color-brand-800': '#48301c',
      '--color-brand-900': '#241b17',
      '--color-ocean-50': '#fbf8f1',
      '--color-ocean-100': '#f3ead8',
      '--color-ocean-200': '#e6d3ae',
      '--color-ocean-300': '#d0b47d',
      '--color-ocean-400': '#b58d4a',
      '--color-ocean-500': '#936a2d',
      '--color-ocean-600': '#755524',
      '--color-ocean-700': '#5c431d',
    },
    shellBackground: {
      background: 'linear-gradient(180deg, rgba(255,250,244,0.95), rgba(248,242,234,0.98))',
    },
    textureOverlay: {
      backgroundImage: 'linear-gradient(rgba(184,105,36,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(147,106,45,0.08) 1px, transparent 1px)',
      backgroundSize: '26px 26px',
      opacity: 0.85,
    },
    heroOverlay: {
      background: 'linear-gradient(100deg, rgba(36,27,23,0.94), rgba(107,63,28,0.78), rgba(213,138,59,0.28))',
    },
    loginShell: {
      background: 'linear-gradient(135deg, #241b17 0%, #6b3f1c 44%, #d58a3b 100%)',
    },
    panelSurface: {
      background: 'linear-gradient(180deg, rgba(255,252,248,0.96), rgba(250,245,237,0.98))',
      border: '1px solid rgba(184,105,36,0.16)',
    },
    sidebarSurface: {
      background: 'linear-gradient(180deg, rgba(255,252,248,0.98), rgba(249,242,232,1))',
    },
    footerSurface: {
      background: 'linear-gradient(135deg, #241b17, #48301c 48%, #6b3f1c 100%)',
    },
    badgeSurface: {
      background: 'rgba(255,249,240,0.16)',
      border: '1px solid rgba(255,255,255,0.14)',
    },
    chipSurface: {
      background: 'rgba(255,248,239,0.92)',
      border: '1px solid rgba(184,105,36,0.18)',
    },
  },
  total_logistics: {
    value: 'total_logistics',
    label: 'Total Logistics',
    description: 'Indigo and cyan network layers for end-to-end logistics groups.',
    sector: 'Total Logistics',
    preview: 'from-indigo-950 via-indigo-700 to-cyan-400',
    cssVars: {
      '--color-brand-50': '#f2f5ff',
      '--color-brand-100': '#e2e8ff',
      '--color-brand-200': '#c6d0ff',
      '--color-brand-300': '#9eafff',
      '--color-brand-400': '#7a87f2',
      '--color-brand-500': '#5d5fd8',
      '--color-brand-600': '#4744b3',
      '--color-brand-700': '#37348c',
      '--color-brand-800': '#272962',
      '--color-brand-900': '#171935',
      '--color-ocean-50': '#effcff',
      '--color-ocean-100': '#d4f7ff',
      '--color-ocean-200': '#acefff',
      '--color-ocean-300': '#73defc',
      '--color-ocean-400': '#3cc4ea',
      '--color-ocean-500': '#219fca',
      '--color-ocean-600': '#207ba1',
      '--color-ocean-700': '#1f6281',
    },
    shellBackground: {
      background: 'linear-gradient(180deg, rgba(244,246,255,0.95), rgba(240,246,255,0.98))',
    },
    textureOverlay: {
      backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(93,95,216,0.10) 0 2px, transparent 2px), radial-gradient(circle at 80% 30%, rgba(60,196,234,0.10) 0 2px, transparent 2px), linear-gradient(135deg, rgba(93,95,216,0.06), transparent 40%)',
      backgroundSize: '52px 52px, 68px 68px, 100% 100%',
      opacity: 0.9,
    },
    heroOverlay: {
      background: 'linear-gradient(98deg, rgba(23,25,53,0.93), rgba(55,52,140,0.76), rgba(60,196,234,0.28))',
    },
    loginShell: {
      background: 'linear-gradient(135deg, #171935 0%, #37348c 46%, #3cc4ea 100%)',
    },
    panelSurface: {
      background: 'linear-gradient(180deg, rgba(251,252,255,0.96), rgba(241,247,255,0.98))',
      border: '1px solid rgba(71,68,179,0.16)',
    },
    sidebarSurface: {
      background: 'linear-gradient(180deg, rgba(251,252,255,0.98), rgba(237,243,255,1))',
    },
    footerSurface: {
      background: 'linear-gradient(135deg, #171935, #272962 50%, #37348c 100%)',
    },
    badgeSurface: {
      background: 'rgba(255,255,255,0.12)',
      border: '1px solid rgba(255,255,255,0.16)',
    },
    chipSurface: {
      background: 'rgba(242,245,255,0.92)',
      border: '1px solid rgba(71,68,179,0.18)',
    },
  },
  freight_forwarder: {
    value: 'freight_forwarder',
    label: 'Freight Forwarder',
    description: 'Navy and ocean palette for freight forwarding, NVOCC and forwarding brands.',
    sector: 'Freight Forwarder',
    preview: 'from-slate-950 via-blue-800 to-cyan-500',
    cssVars: {
      '--color-brand-50': '#eff4ff',
      '--color-brand-100': '#dfe8ff',
      '--color-brand-200': '#bfd2ff',
      '--color-brand-300': '#90b0ff',
      '--color-brand-400': '#6687f2',
      '--color-brand-500': '#4360d2',
      '--color-brand-600': '#2b4ab3',
      '--color-brand-700': '#1f398d',
      '--color-brand-800': '#15295f',
      '--color-brand-900': '#0b1838',
      '--color-ocean-50': '#effcff',
      '--color-ocean-100': '#cff8ff',
      '--color-ocean-200': '#9febfb',
      '--color-ocean-300': '#5dd7ef',
      '--color-ocean-400': '#29b8da',
      '--color-ocean-500': '#1295ba',
      '--color-ocean-600': '#127493',
      '--color-ocean-700': '#125c75',
    },
    shellBackground: {
      background: 'linear-gradient(180deg, rgba(241,246,255,0.95), rgba(239,246,252,0.98))',
    },
    textureOverlay: {
      backgroundImage: 'radial-gradient(circle, rgba(18,149,186,0.12) 1px, transparent 1px)',
      backgroundSize: '24px 24px',
      opacity: 0.7,
    },
    heroOverlay: {
      background: 'linear-gradient(100deg, rgba(11,24,56,0.94), rgba(31,57,141,0.78), rgba(41,184,218,0.24))',
    },
    loginShell: {
      background: 'linear-gradient(135deg, #0b1838 0%, #1f398d 46%, #29b8da 100%)',
    },
    panelSurface: {
      background: 'linear-gradient(180deg, rgba(251,253,255,0.96), rgba(241,247,255,0.98))',
      border: '1px solid rgba(43,74,179,0.16)',
    },
    sidebarSurface: {
      background: 'linear-gradient(180deg, rgba(251,253,255,0.98), rgba(236,244,255,1))',
    },
    footerSurface: {
      background: 'linear-gradient(135deg, #0b1838, #15295f 50%, #1f398d 100%)',
    },
    badgeSurface: {
      background: 'rgba(255,255,255,0.12)',
      border: '1px solid rgba(255,255,255,0.16)',
    },
    chipSurface: {
      background: 'rgba(239,244,255,0.92)',
      border: '1px solid rgba(43,74,179,0.18)',
    },
  },
};

export const PORTAL_THEME_OPTIONS: PortalThemeOption[] = Object.values(THEMES).map((theme) => ({
  value: theme.value,
  label: theme.label,
  description: theme.description,
  sector: theme.sector,
  preview: theme.preview,
}));

export function resolvePortalTheme(template?: string | null, visual?: StorefrontVisualThemeInput | null): PortalThemeDefinition {
  const base = template ? (THEMES[template as PortalThemeTemplate] ?? THEMES.freight_forwarder) : THEMES.freight_forwarder;
  if (!visual?.palette) return base;

  const palette = resolveRuntimePalette(visual.palette);
  const tone = visual.tone ?? 'corporate';
  const heroOpacity = tone === 'premium' ? 0.92 : tone === 'friendly' ? 0.82 : 0.88;

  return {
    ...base,
    preview: `from-[${palette.surfaceStrong}] via-[${palette.motifMain}] to-[${palette.motifSoft}]`,
    cssVars: {
      ...base.cssVars,
      ...palette.brandVars,
      '--color-ocean-50': palette.surface,
      '--color-ocean-100': palette.surfaceSoft,
      '--color-ocean-200': palette.motifMuted,
      '--color-ocean-300': palette.motifMuted,
      '--color-ocean-400': palette.motifSoft,
      '--color-ocean-500': palette.motifSoft,
      '--color-ocean-600': palette.motifMain,
      '--color-ocean-700': palette.surfaceStrong,
    },
    shellBackground: {
      background: `linear-gradient(180deg, ${alpha(palette.surface, 0.96)}, ${alpha(palette.surfaceSoft, 0.72)})`,
    },
    textureOverlay: {
      backgroundImage: visual.visualPattern === 'minimal_grid'
        ? `linear-gradient(${alpha(palette.motifMain, 0.08)} 1px, transparent 1px), linear-gradient(90deg, ${alpha(palette.motifMain, 0.06)} 1px, transparent 1px)`
        : `radial-gradient(circle at 24px 24px, ${alpha(palette.motifMain, 0.12)} 0 2px, transparent 2px), linear-gradient(120deg, ${alpha(palette.motifSoft, 0.08)}, transparent 45%)`,
      backgroundSize: visual.visualPattern === 'minimal_grid' ? '34px 34px' : '58px 58px, 100% 100%',
      opacity: tone === 'operations_first' ? 0.55 : 0.72,
    },
    heroOverlay: {
      background: `linear-gradient(100deg, ${alpha(palette.surfaceStrong, heroOpacity)}, ${alpha(palette.motifMain, 0.74)}, ${alpha(palette.motifSoft, 0.26)})`,
    },
    loginShell: {
      background: `linear-gradient(135deg, ${palette.surfaceStrong} 0%, ${palette.motifMain} 48%, ${palette.motifSoft} 100%)`,
    },
    panelSurface: {
      background: `linear-gradient(180deg, rgba(255,255,255,0.96), ${alpha(palette.surface, 0.94)})`,
      border: `1px solid ${alpha(palette.motifMain, 0.16)}`,
    },
    sidebarSurface: {
      background: `linear-gradient(180deg, rgba(255,255,255,0.98), ${alpha(palette.surface, 0.96)})`,
    },
    footerSurface: {
      background: `linear-gradient(135deg, ${palette.surfaceStrong}, ${palette.motifMain} 62%, ${palette.surfaceStrong})`,
    },
    badgeSurface: {
      background: 'rgba(255,255,255,0.14)',
      border: '1px solid rgba(255,255,255,0.18)',
    },
    chipSurface: {
      background: alpha(palette.surface, 0.92),
      border: `1px solid ${alpha(palette.motifMain, 0.18)}`,
    },
  };
}
